import { Button, FormDataConsumer, SelectInput } from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { find, findIndex } from "lodash";
import AddressAutoCompleteInput from "./AddressAutoCompleteInput";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import StudentAddressAutoCompleteInput from "./StudentAddressAutoCompleteInput";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
});

interface ChangeStudentAddressButtonProps {
  studentId: number;
}

const ChangeStudentAddressButton: React.FC<ChangeStudentAddressButtonProps> = ({
  studentId,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);

  const studentsField = useField("students");
  const routesField = useField("routes");
  const formStudentsChangedField = useField("formStudentsChanged");

  const newStudentAddress = useField("newStudentAddress");
  const locationData = useField("locationData");
  const addressTypeField = useField("addressType");

  const classes = useStyles();

  const handleOpenDialog = () => {
    const findStudent = find(studentsField.input.value, ["id", studentId]);
    setSelectedStudent(findStudent);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    newStudentAddress.input.onChange(null);
    locationData.input.onChange(null);
    addressTypeField.input.onChange(null);
  };

  const handleSaveChangeAddress = () => {
    if (addressTypeField.input.value === "custom") {
      const { BLK_NO, BUILDING, LATITUDE, LONGITUDE, POSTAL, ROAD_NAME } =
        locationData.input.value;

      selectedStudent.address.blockNumber = BLK_NO;
      selectedStudent.address.building = BUILDING;
      selectedStudent.address.postalCode = POSTAL;
      selectedStudent.address.street = ROAD_NAME;
      selectedStudent.address.lat = LATITUDE;
      selectedStudent.address.lng = LONGITUDE;
      selectedStudent.address.unitNumber = "";
    }

    if (addressTypeField.input.value === "existing") {
      const {
        blockNumber,
        building,
        postalCode,
        street,
        lat,
        lng,
        unitNumber,
      } = locationData.input.value;

      selectedStudent.address.blockNumber = blockNumber;
      selectedStudent.address.building = building;
      selectedStudent.address.postalCode = postalCode;
      selectedStudent.address.street = street;
      selectedStudent.address.lat = lat;
      selectedStudent.address.lng = lng;
      selectedStudent.address.unitNumber = unitNumber;
    }

    const findStudentIndex = findIndex(studentsField.input.value, [
      "id",
      studentId,
    ]);

    studentsField.input.onChange([
      ...studentsField.input.value.slice(0, findStudentIndex),
      selectedStudent,
      ...studentsField.input.value.slice(findStudentIndex + 1),
    ]);
    formStudentsChangedField.input.onChange(true);

    handleClose();
  };

  return (
    <>
      <StyledButton variant="outlined" onClick={handleOpenDialog}>
        Change Address
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Change Address of {selectedStudent?.firstName}{" "}
          {selectedStudent?.lastName}
        </DialogTitle>
        <DialogContent>
          {routesField.input.value.length > 0 && (
            <Alert severity="warning" style={{ marginBottom: "14px" }}>
              <AlertTitle>Warning</AlertTitle>

              <Typography variant="body2">
                Please be advised that any changes to the student list or
                address will reset all existing route data.
              </Typography>
            </Alert>
          )}

          <SelectInput
            label="Select Address Type"
            source="addressType"
            choices={[
              { id: "existing", name: "Existing Student Address" },
              { id: "custom", name: "Custom Address with Postal" },
            ]}
          />

          <FormDataConsumer>
            {({ formData }) => {
              if (formData.addressType === "existing") {
                return (
                  <StudentAddressAutoCompleteInput
                    studentId={selectedStudent?.id}
                    label="Select Address"
                    source="newStudentAddress"
                    referenceSource="locationData"
                  />
                );
              }

              if (formData.addressType === "custom") {
                return (
                  <AddressAutoCompleteInput
                    label="Select Address"
                    source="newStudentAddress"
                    referenceSource="locationData"
                  />
                );
              }

              return <></>;
            }}
          </FormDataConsumer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveChangeAddress}
            color="primary"
            label="Save"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeStudentAddressButton;
